import * as amplitude from "@amplitude/analytics-browser";
import { Heading, HStack, Spacer } from "@chakra-ui/layout";
import { Button, Flex } from "@chakra-ui/react";
import { useFlags } from "flagsmith/react";
import { Formik } from "formik";
import { useMemo } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { GenericError } from "src/components/Feedback/GenericError";
import { CheckboxGroup } from "src/components/Inputs/CheckboxGroup";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { useEnrollmentPeriod } from "src/components/Providers/EnrollmentPeriodProvider";
import { useFormTemplates } from "src/components/Providers/FormTemplateProvider";
import { FILTER_DELIMITER, SearchAndFilterTypes } from "src/constants";
import { useOrganization } from "src/hooks/useOrganization";
import { useSchoolAdmin } from "src/hooks/useSchoolAdmin";
import { useTags } from "src/scenes/orgAdmin/tags/useTags";
import * as breadcrumb from "src/services/breadcrumb";
import * as Url from "src/services/url";
import { getAllColumnIds } from "../formTables/columns";
import { ALL_FORM_LIST_COLUMNS } from "../formTables/constants";
import { getAllColumnGroups } from "./utils";

export interface AdvancedViewSettingsFormType {
  [key: string]: boolean;
}

export interface AdvancedViewSettingsState {
  questionIdsAndLabelsInOrder: {
    id: uuid;
    label: string;
    isArchived: boolean;
  }[];
  disclaimerIdsAndLabels: { id: uuid; label: string }[];
  formTemplateConfig: {
    isLotteryEnabled: boolean;
    isRankingEnabled: boolean;
  };
}

export function AdvancedViewSettings() {
  const organization = useOrganization();
  const orgPath = organization?.hasData() ? organization.data.path : "";
  const { selectedNavFormTemplate } = useFormTemplates();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { formTemplateId } = useParams();

  const flags = useFlags(["current-applying-schools"]);
  const isCurrentApplyingSchoolsEnabled =
    flags["current-applying-schools"].enabled;

  const { isSchoolAdmin } = useSchoolAdmin();

  const { selectedEnrollmentPeriod } = useEnrollmentPeriod();
  const { tagGroups } = useTags({
    enrollmentPeriodId: selectedEnrollmentPeriod?.id ?? "",
    tagGroupId: null,
    isEditing: false,
  });

  const initialValues = useMemo(() => {
    const visibleColumns = searchParams.get(
      SearchAndFilterTypes.VisibleColumns
    );
    if (visibleColumns) {
      return visibleColumns.split(FILTER_DELIMITER).reduce((acc, column) => {
        acc[column] = true;
        return acc;
      }, {} as { [key: string]: boolean });
    }
    return {};
  }, [searchParams]);

  const {
    questionIdsAndLabelsInOrder,
    disclaimerIdsAndLabels,
    formTemplateConfig,
  } = (location.state ?? {}) as Partial<AdvancedViewSettingsState>;
  if (
    !questionIdsAndLabelsInOrder ||
    !disclaimerIdsAndLabels ||
    !formTemplateConfig
  ) {
    return <GenericError />;
  }

  const breadcrumbItems: breadcrumb.BreadcrumbType[] = [
    ...breadcrumb.form.getBreadcrumbsForList(
      organization,
      selectedNavFormTemplate
    ),
    { label: "Advanced view settings", href: "" },
  ];

  const columnGroups = getAllColumnGroups({
    isLotteryEnabled: formTemplateConfig.isLotteryEnabled,
    isRankingEnabled: !isSchoolAdmin && formTemplateConfig.isRankingEnabled,
    isCurrentApplyingSchoolsEnabled,
    orgPath,
    tagGroups: tagGroups.map((tagGroup) => ({
      id: tagGroup.id,
      label: tagGroup.name,
    })),
    questionIdsAndLabelsInOrder,
    disclaimerIdsAndLabels,
  });

  const handleSubmit = (values: AdvancedViewSettingsFormType) => {
    // Get the selected columns in order
    const allColumnIds = getAllColumnIds(
      ALL_FORM_LIST_COLUMNS,
      tagGroups,
      questionIdsAndLabelsInOrder,
      disclaimerIdsAndLabels
    );
    const selectedColumnsInOrder = allColumnIds.filter(
      (column) => values[column]
    );

    // Update the search params with the selected columns
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set(
      SearchAndFilterTypes.VisibleColumns,
      selectedColumnsInOrder.join(FILTER_DELIMITER)
    );

    // Navigate back to the form list with updated parameters
    navigate(
      organization
        .map((org) =>
          Url.OrgAdmin.Forms.index({
            organization: org,
            formTemplateId: formTemplateId ?? "",
            params: newSearchParams.toString(),
          })
        )
        .withDefault("#"),
      { replace: true }
    );
  };

  return (
    <Flex direction="column" gap="0.5rem" minBlockSize="100%">
      <Breadcrumb items={breadcrumbItems} mb={4} />
      <HStack pb={8}>
        <Heading as="h1" size="lg">
          Select columns
        </Heading>
      </HStack>
      <Formik<AdvancedViewSettingsFormType>
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, submitForm }) => (
          <>
            {columnGroups.map((group) => (
              <CheckboxGroup
                key={group.groupName}
                groupName={group.groupName}
                options={group.options}
                values={values}
                onChange={(key, selected) => {
                  amplitude.track("Changed column in view", {
                    section_name: group.groupName,
                    view_column_name:
                      group.options.find((option) => option.key === key)
                        ?.label ?? key,
                    active_column: selected ? "is_active" : "is_inactive",
                    checkbox_location: "custom_columns",
                  });
                  setFieldValue(key, selected);
                }}
                columnWidthMin={group.columnWidthMin}
              />
            ))}
            <Spacer />
            <AdminFormButtons>
              <Button
                variant="outline"
                colorScheme="gray"
                onClick={() => {
                  amplitude.track("Clicked Back");
                  navigate(-1);
                }}
              >
                Back
              </Button>
              <Spacer />
              <Button
                variant="solid"
                onClick={() => {
                  amplitude.track("Clicked Update view columns");
                  submitForm();
                }}
              >
                Update view
              </Button>
            </AdminFormButtons>
          </>
        )}
      </Formik>
    </Flex>
  );
}
